import { motion } from "framer-motion";
import { Bot, Workflow, Brain, MessagesSquare, LineChart, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SectionHeader from "@/components/SectionHeader";
import CTASection from "@/components/CTASection";

const AGENTS = [
  { name: "Research Agent", icon: Brain, desc: "Account & contact research, news triggers, and ICP scoring — across millions of accounts in minutes." },
  { name: "Outbound Agent", icon: MessagesSquare, desc: "Personalized emails, LinkedIn touches, and follow-ups generated from real-time signals." },
  { name: "Meeting Prep Agent", icon: Workflow, desc: "Briefs your AEs before every call — company snapshot, stakeholders, prior touchpoints, and talk track." },
  { name: "Pipeline Analyst", icon: LineChart, desc: "Watches your pipeline 24/7, flags stalled deals, and suggests next-best-actions to AEs and managers." },
  { name: "RevOps Agent", icon: ShieldCheck, desc: "Keeps CRM clean — dedupes, enriches, updates fields, and ensures forecast hygiene automatically." },
  { name: "Conversation Intelligence", icon: Bot, desc: "Listens to calls, extracts objections, coaches reps, and feeds insight back into messaging and battle cards." },
];

const HERO_IMG = "https://images.pexels.com/photos/17485657/pexels-photo-17485657.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function AIRevenueAgents() {
  return (
    <div data-testid="page-ai-agents">
      <section className="relative bg-[#0A1F44] text-white overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-[0.08] pointer-events-none" />
        <div className="sozos-container relative pt-16 md:pt-24 pb-20 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.24em] text-blue-300 font-semibold">AI Revenue Agents</p>
              <motion.h1
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-display text-5xl sm:text-6xl lg:text-7xl mt-5 tracking-tight leading-[1.02] text-white"
              >
                An <span className="italic text-blue-300">AI workforce</span> for revenue teams.
              </motion.h1>
              <p className="text-lg md:text-xl text-slate-300 mt-7 max-w-2xl leading-relaxed">
                Our agentic AI runs research, outbound, follow-ups, CRM hygiene, and conversation intelligence —
                freeing your humans to do what only humans can do: build trust and close.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link to="/contact#book" data-testid="ai-cta-book" className="btn-primary">
                  Book a Growth Consultation <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/services" data-testid="ai-cta-services" className="btn-ghost-white">
                  Explore Services
                </Link>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5"
            >
              <img src={HERO_IMG} alt="AI neural network visualization" className="w-full h-[420px] object-cover rounded-sm border border-white/10" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="sozos-container section-pad">
          <SectionHeader
            eyebrow="Agent stack"
            title="Six purpose-built agents working as one team."
            description="Each agent owns a clear job. Together, they create a closed-loop revenue system that learns and compounds."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {AGENTS.map((a, i) => (
              <motion.div
                key={a.name}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`agent-card-${i}`}
                className="bg-white border border-slate-200 rounded-md p-7 card-hover"
              >
                <div className="w-11 h-11 bg-[#1E40AF] text-white rounded-sm flex items-center justify-center">
                  <a.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl text-[#0A1F44] mt-5 tracking-tight">{a.name}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{a.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="sozos-container section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="eyebrow">Human + AI, not Human vs AI</p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#0A1F44] mt-3 tracking-tight leading-[1.05]">
                AI handles volume. People handle judgment.
              </h2>
              <p className="text-slate-600 mt-5 text-base md:text-lg leading-relaxed">
                Our model pairs AI agents with senior SDRs and AEs — agents own the high-volume,
                low-judgment work; humans own discovery, framing, and closing. The result: a 10x
                productivity lift without losing the trust that closes enterprise deals.
              </p>
              <ul className="mt-7 space-y-3 text-sm text-slate-700">
                {["Reduce SDR ramp from 4 months to 4 weeks", "Cut research time from 30 min to 30 seconds", "Increase reply rates with signal-based personalization", "Maintain CRM hygiene without rep effort"].map((t) => (
                  <li key={t} className="flex items-start gap-3"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#1E40AF]" /> {t}</li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { k: "10X", v: "Rep productivity" },
                { k: "60%", v: "Lower outreach cost" },
                { k: "4 wks", v: "SDR ramp" },
                { k: "99%", v: "CRM hygiene" },
              ].map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="bg-white border border-slate-200 rounded-md p-7"
                >
                  <div className="font-display text-5xl text-[#0A1F44]">{m.k}</div>
                  <div className="text-xs uppercase tracking-widest text-[#1E40AF] font-semibold mt-3">{m.v}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection title="Deploy your AI revenue workforce in weeks." subtitle="AI revenue agents" />
    </div>
  );
}
